import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, deleteDoc, doc, updateDoc, where, orderBy, Timestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';

/**
 * Video Call Recording Management
 * สำหรับ Admin จัดการ Video Call recordings
 * - ดูรายการ recordings
 * - ลบ recordings ที่หมดอายุ
 * - ลบ recordings ที่เข้าเงื่อนไข
 * - จัดการ storage
 */
const VideoCallRecordingManagement = () => {
  const { currentUser } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, expired, toDelete
  const [selectedRecordings, setSelectedRecordings] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    toDelete: 0,
    totalSize: 0
  });

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.isAdmin)) {
      loadRecordings();
    }
  }, [currentUser, filter]);

  const loadRecordings = async () => {
    try {
      setLoading(true);
      const recordingsRef = collection(db, 'videoCallRecordings');
      let q;

      if (filter === 'expired') {
        q = query(
          recordingsRef,
          where('status', '==', 'expired'),
          orderBy('expiresAt', 'desc')
        );
      } else if (filter === 'toDelete') {
        // Recordings ที่หมดอายุและต้องลบ
        const now = Timestamp.now();
        q = query(
          recordingsRef,
          where('expiresAt', '<=', now),
          where('status', '==', 'active'),
          orderBy('expiresAt', 'asc')
        );
      } else if (filter === 'active') {
        q = query(
          recordingsRef,
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(recordingsRef, orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      const recordingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setRecordings(recordingsData);
      calculateStats(recordingsData);
    } catch (error) {
      console.error('Error loading recordings:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (recordingsData) => {
    const stats = {
      total: recordingsData.length,
      active: 0,
      expired: 0,
      toDelete: 0,
      totalSize: 0
    };

    const now = Timestamp.now();

    recordingsData.forEach(recording => {
      if (recording.status === 'active') {
        stats.active++;
        // ตรวจสอบว่าหมดอายุหรือยัง
        if (recording.expiresAt && recording.expiresAt <= now) {
          stats.toDelete++;
        }
      } else if (recording.status === 'expired') {
        stats.expired++;
      }

      if (recording.fileSize) {
        stats.totalSize += recording.fileSize;
      }
    });

    setStats(stats);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (recording) => {
    if (!recording.expiresAt) return false;
    const now = Timestamp.now();
    return recording.expiresAt <= now;
  };

  const handleDeleteRecording = async (recordingId, recordingUrl) => {
    if (!window.confirm('คุณต้องการลบ recording นี้หรือไม่?\n\nการลบจะลบทั้งไฟล์ video และข้อมูลในฐานข้อมูล')) {
      return;
    }

    try {
      // ลบไฟล์จาก Storage
      if (recordingUrl) {
        try {
          const storageRef = ref(storage, recordingUrl);
          await deleteObject(storageRef);
        } catch (error) {
          console.error('Error deleting file from storage:', error);
          // ยังคงลบข้อมูลในฐานข้อมูลต่อ
        }
      }

      // ลบ thumbnail ถ้ามี
      // TODO: ลบ thumbnail ถ้ามี

      // ลบข้อมูลในฐานข้อมูล
      await deleteDoc(doc(db, 'videoCallRecordings', recordingId));

      alert('ลบ recording สำเร็จ');
      loadRecordings();
    } catch (error) {
      console.error('Error deleting recording:', error);
      alert('เกิดข้อผิดพลาดในการลบ');
    }
  };

  const handleDeleteExpired = async () => {
    const expiredRecordings = recordings.filter(r => 
      r.status === 'active' && isExpired(r)
    );

    if (expiredRecordings.length === 0) {
      alert('ไม่มี recordings ที่หมดอายุ');
      return;
    }

    if (!window.confirm(`คุณต้องการลบ recordings ที่หมดอายุทั้งหมด ${expiredRecordings.length} รายการหรือไม่?`)) {
      return;
    }

    try {
      let deleted = 0;
      let errors = 0;

      for (const recording of expiredRecordings) {
        try {
          // ลบไฟล์จาก Storage
          if (recording.recordingUrl) {
            try {
              const storageRef = ref(storage, recording.recordingUrl);
              await deleteObject(storageRef);
            } catch (error) {
              console.error('Error deleting file:', error);
            }
          }

          // อัพเดท status เป็น deleted
          await updateDoc(doc(db, 'videoCallRecordings', recording.id), {
            status: 'deleted',
            deletedAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          });

          deleted++;
        } catch (error) {
          console.error('Error deleting recording:', error);
          errors++;
        }
      }

      alert(`ลบสำเร็จ: ${deleted} รายการ\nเกิดข้อผิดพลาด: ${errors} รายการ`);
      loadRecordings();
    } catch (error) {
      console.error('Error deleting expired recordings:', error);
      alert('เกิดข้อผิดพลาดในการลบ');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecordings.length === 0) {
      alert('กรุณาเลือก recordings ที่ต้องการลบ');
      return;
    }

    if (!window.confirm(`คุณต้องการลบ ${selectedRecordings.length} รายการหรือไม่?`)) {
      return;
    }

    try {
      let deleted = 0;
      let errors = 0;

      for (const recordingId of selectedRecordings) {
        const recording = recordings.find(r => r.id === recordingId);
        if (!recording) continue;

        try {
          // ลบไฟล์จาก Storage
          if (recording.recordingUrl) {
            try {
              const storageRef = ref(storage, recording.recordingUrl);
              await deleteObject(storageRef);
            } catch (error) {
              console.error('Error deleting file:', error);
            }
          }

          // ลบข้อมูลในฐานข้อมูล
          await deleteDoc(doc(db, 'videoCallRecordings', recordingId));
          deleted++;
        } catch (error) {
          console.error('Error deleting recording:', error);
          errors++;
        }
      }

      alert(`ลบสำเร็จ: ${deleted} รายการ\nเกิดข้อผิดพลาด: ${errors} รายการ`);
      setSelectedRecordings([]);
      loadRecordings();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('เกิดข้อผิดพลาดในการลบ');
    }
  };

  const handleToggleSelect = (recordingId) => {
    if (selectedRecordings.includes(recordingId)) {
      setSelectedRecordings(selectedRecordings.filter(id => id !== recordingId));
    } else {
      setSelectedRecordings([...selectedRecordings, recordingId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRecordings.length === recordings.length) {
      setSelectedRecordings([]);
    } else {
      setSelectedRecordings(recordings.map(r => r.id));
    }
  };

  if (!currentUser || (currentUser.role !== 'admin' && !currentUser.isAdmin)) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600">❌ คุณไม่มีสิทธิ์เข้าถึงหน้านี้</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">📹 Video Call Recording Management</h2>
        <div className="flex gap-2">
          {filter === 'toDelete' && (
            <button
              onClick={handleDeleteExpired}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
            >
              🗑️ ลบที่หมดอายุทั้งหมด
            </button>
          )}
          {selectedRecordings.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
            >
              🗑️ ลบที่เลือก ({selectedRecordings.length})
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">ทั้งหมด</div>
          <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">ใช้งานอยู่</div>
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">หมดอายุ</div>
          <div className="text-2xl font-bold text-gray-600">{stats.expired}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">ต้องลบ</div>
          <div className="text-2xl font-bold text-red-600">{stats.toDelete}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">ขนาดรวม</div>
          <div className="text-2xl font-bold text-blue-600">{formatFileSize(stats.totalSize)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg transition ${
            filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          ทั้งหมด
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 rounded-lg transition ${
            filter === 'active' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          ใช้งานอยู่
        </button>
        <button
          onClick={() => setFilter('toDelete')}
          className={`px-4 py-2 rounded-lg transition ${
            filter === 'toDelete' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          ต้องลบ ({stats.toDelete})
        </button>
        <button
          onClick={() => setFilter('expired')}
          className={`px-4 py-2 rounded-lg transition ${
            filter === 'expired' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          หมดอายุแล้ว
        </button>
      </div>

      {/* Recordings Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>
      ) : recordings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">ไม่พบ recordings</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRecordings.length === recordings.length && recordings.length > 0}
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seller</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ระยะเวลา</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ขนาด</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">หมดอายุ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จัดการ</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recordings.map(recording => {
                const expired = isExpired(recording);
                const isSelected = selectedRecordings.includes(recording.id);

                return (
                  <tr
                    key={recording.id}
                    className={expired && recording.status === 'active' ? 'bg-red-50' : ''}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(recording.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recording.callId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recording.storeId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recording.buyerId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recording.sellerId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recording.duration ? `${Math.round(recording.duration / 60)} นาที` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(recording.fileSize)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(recording.expiresAt)}
                      {expired && recording.status === 'active' && (
                        <span className="ml-2 text-red-600 font-bold">⚠️ หมดอายุ</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        recording.status === 'active'
                          ? expired
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                          : recording.status === 'expired'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {recording.status === 'active' && expired
                          ? '⚠️ หมดอายุ'
                          : recording.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteRecording(recording.id, recording.recordingUrl)}
                        className="text-red-600 hover:text-red-800"
                      >
                        🗑️ ลบ
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VideoCallRecordingManagement;

